import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";
import { Package, Calendar, MapPin, XCircle, Star, X, MessageSquarePlus, Trash2, AlertTriangle, FileText, Download } from "lucide-react";

const STATUS_COLORS = {
  pending: "#FFC500", processing: "#00A6E0", shipped: "#B845FF",
  completed: "#00E0AA", cancelled: "#FF1F87",
};

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} type="button" onClick={() => onChange(s)}
          onMouseEnter={() => setHovered(s)} onMouseLeave={() => setHovered(0)}
          className="focus:outline-none">
          <Star className="w-7 h-7 transition-colors"
            fill={(hovered || value) >= s ? "#FFC500" : "none"}
            stroke={(hovered || value) >= s ? "#FFC500" : "#d1d5db"} />
        </button>
      ))}
    </div>
  );
}

function ReviewModal({ item, orderId, onClose, onSuccess }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please select a star rating"); return; }
    if (!text.trim()) { toast.error("Please write a short comment"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${item.product_id}/reviews`, { rating, text: text.trim() });
      toast.success("Review submitted! Thank you.");
      onSuccess(item.product_id);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl border-[3px] border-black shadow-[6px_6px_0_#000] p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-['Fredoka'] font-bold text-xl">Leave a Review</h3>
            <p className="text-sm c4t-desc mt-0.5">{item.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="c4t-label">Star Rating *</label>
            <StarRating value={rating} onChange={setRating} />
          </div>
          <div>
            <label className="c4t-label">Your Review *</label>
            <textarea className="c4t-input" rows={4}
              placeholder="Tell us what you loved about this product..."
              value={text} onChange={e => setText(e.target.value)} maxLength={2000} />
            <p className="text-xs text-gray-400 mt-1 text-right">{text.length}/2000</p>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={submitting} className="c4t-btn !py-2 !px-5 flex-1">
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
            <button type="button" onClick={onClose} className="c4t-pill !py-2 !px-4">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteAccountModal({ onClose, onConfirm, deleting }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl border-[3px] border-black shadow-[6px_6px_0_#000] p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-[#FF1F87]" />
            <h3 className="font-['Fredoka'] font-bold text-xl">Delete Account</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="bg-[#FFF0F5] border-[2px] border-[#FF1F87] rounded-2xl p-4 mb-5">
          <p className="text-sm font-semibold text-[#FF1F87] mb-2">This is permanent and cannot be undone.</p>
          <ul className="text-sm space-y-1 text-gray-700 list-disc list-inside">
            <li>Your account and login will be removed</li>
            <li>Your cart will be cleared</li>
            <li>Your order history will be anonymised</li>
          </ul>
        </div>
        <label className="flex items-start gap-3 mb-5 cursor-pointer">
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#FF1F87] cursor-pointer" />
          <span className="text-sm font-semibold">I understand this is permanent and want to delete my account</span>
        </label>
        <div className="flex gap-3">
          <button onClick={onConfirm} disabled={!confirmed || deleting}
            className="flex-1 bg-[#FF1F87] text-white font-bold border-[2px] border-black rounded-xl px-5 py-2.5 text-sm shadow-[2px_2px_0_#000] hover:translate-y-[1px] hover:shadow-[1px_1px_0_#000] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <Trash2 className="w-4 h-4" />
            {deleting ? "Deleting…" : "Delete My Account"}
          </button>
          <button onClick={onClose} disabled={deleting} className="c4t-pill !py-2 !px-5 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const { user, loading, deleteAccount } = useAuth();
  const { format } = useCurrency();
  const [activeTab, setActiveTab] = useState("physical");
  const [orders, setOrders] = useState([]);
  const [digitalOrders, setDigitalOrders] = useState([]);
  const [busy, setBusy] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewed, setReviewed] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadOrders = () => {
    setBusy(true);
    Promise.all([
      api.get("/orders/mine"),
      api.get("/digital-orders/mine"),
    ]).then(([phys, dig]) => {
      setOrders(phys.data);
      setDigitalOrders(dig.data);
    }).catch(() => {
      api.get("/orders/mine").then(r => setOrders(r.data));
    }).finally(() => setBusy(false));
  };

  useEffect(() => {
    document.title = "My Account – Cheap4Teach";
    if (user) loadOrders();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelOrder = async (id) => {
    if (!window.confirm("Cancel this order? This cannot be undone.")) return;
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.success("Order cancelled");
      loadOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel order");
    }
  };

  const handleDownload = async (productId, productName) => {
    setDownloading(prev => ({ ...prev, [productId]: true }));
    try {
      const r = await api.get(`/digital-products/${productId}/download`);
      const { download_url, file_name } = r.data;
      // Open in new tab — fresh presigned URL regenerated each time
      const a = document.createElement("a");
      a.href = download_url;
      a.download = file_name || `${productName}.pdf`;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Download started!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed. Please contact support.");
    } finally {
      setDownloading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const markReviewed = (productId) => setReviewed(prev => new Set([...prev, productId]));

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account. Please try again.");
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="account-page">
      <div className="flex items-center gap-4 mb-8">
        {user.picture && <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-[3px] border-black" />}
        <div>
          <h1 className="text-3xl sm:text-4xl font-['Fredoka'] font-bold">Hi, {user.name}!</h1>
          <p className="c4t-desc">{user.email}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b-2 border-black/10">
        <button
          onClick={() => setActiveTab("physical")}
          className={`flex items-center gap-2 px-5 py-2.5 font-['Fredoka'] font-bold text-lg rounded-t-xl transition-colors ${activeTab === "physical" ? "bg-[#FF1F87] text-white border-2 border-b-0 border-black" : "text-gray-500 hover:text-[#FF1F87]"}`}
        >
          <Package className="w-5 h-5" /> My Orders
          {orders.length > 0 && <span className="text-sm font-bold bg-white/30 rounded-full px-1.5">{orders.length}</span>}
        </button>
        <button
          onClick={() => setActiveTab("digital")}
          className={`flex items-center gap-2 px-5 py-2.5 font-['Fredoka'] font-bold text-lg rounded-t-xl transition-colors ${activeTab === "digital" ? "bg-[#FF1F87] text-white border-2 border-b-0 border-black" : "text-gray-500 hover:text-[#FF1F87]"}`}
        >
          <FileText className="w-5 h-5" /> Digital Downloads
          {digitalOrders.length > 0 && <span className="text-sm font-bold bg-white/30 rounded-full px-1.5">{digitalOrders.length}</span>}
        </button>
      </div>

      {busy ? <p className="c4t-desc">Loading orders...</p> : (
        <>
          {/* ── Physical Orders Tab ── */}
          {activeTab === "physical" && (
            orders.length === 0 ? (
              <div className="c4t-card text-center py-12" data-testid="account-no-orders">
                <p className="text-lg c4t-desc">You haven't placed any orders yet.</p>
                <Link to="/shop" className="mt-4 inline-flex c4t-btn c4t-btn-pink">Start Shopping</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map(o => (
                  <div key={o.id} className="c4t-card" data-testid={`account-order-${o.id}`}>
                    <div className="flex flex-wrap justify-between gap-3">
                      <div>
                        <p className="font-['Fredoka'] font-bold text-lg">Order #{o.id.slice(0, 8).toUpperCase()}</p>
                        <p className="text-sm c4t-desc flex items-center gap-2"><Calendar className="w-4 h-4" /> {new Date(o.created_at).toLocaleDateString()}</p>
                        <p className="text-sm c4t-desc flex items-center gap-2"><MapPin className="w-4 h-4" /> {o.country}</p>
                      </div>
                      <div className="text-right">
                        <span className="c4t-badge" style={{ background: STATUS_COLORS[o.status] || "#FFC500", color: o.status === "pending" ? "#000" : "#fff" }}>{o.status}</span>
                        <p className="text-2xl font-bold text-[#FF1F87] mt-2">{format(o.total)}</p>
                        {o.payment_method === "wipay" && o.payment_status === "paid" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1" style={{background:"#dcfce7",color:"#15803d"}}>Paid by Card (WiPay)</span>
                        ) : o.payment_method === "wipay" && o.payment_status === "pending" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1" style={{background:"#fff7ed",color:"#c2410c"}}>Card Payment Pending</span>
                        ) : o.payment_method === "wipay" && o.payment_status === "failed" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1" style={{background:"#fee2e2",color:"#b91c1c"}}>Card Payment Failed</span>
                        ) : o.payment_method === "bank_transfer" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full mt-1" style={{background:"#fef9c3",color:"#92400e"}}>Bank Transfer</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 border-t border-black/10 pt-3 space-y-2">
                      {o.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3 flex-wrap">
                          <span className="text-sm c4t-desc">{item.quantity} × {item.name} ({format(item.price * item.quantity)})</span>
                          {o.status === "completed" && item.product_id && !reviewed.has(item.product_id) && (
                            <button onClick={() => setReviewModal({ item, orderId: o.id })}
                              className="c4t-pill !py-1 !px-3 !text-xs flex items-center gap-1 !bg-[#FFF9F2] !border-[#FFC500] text-[#92400e]">
                              <MessageSquarePlus className="w-3 h-3" /> Write Review
                            </button>
                          )}
                          {o.status === "completed" && item.product_id && reviewed.has(item.product_id) && (
                            <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                              <Star className="w-3 h-3 fill-[#FFC500] stroke-[#FFC500]" /> Reviewed!
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {o.status === "pending" && (
                      <button onClick={() => cancelOrder(o.id)} className="mt-3 c4t-pill !bg-[#FF1F87] !text-white !py-2 !px-3" data-testid={`cancel-order-${o.id}`}>
                        <XCircle className="w-4 h-4" /> Cancel Order
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── Digital Downloads Tab ── */}
          {activeTab === "digital" && (
            digitalOrders.length === 0 ? (
              <div className="c4t-card text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-lg c4t-desc">No digital purchases yet.</p>
                <Link to="/digital-shop" className="mt-4 inline-flex c4t-btn c4t-btn-pink">Visit Digital Shop</Link>
              </div>
            ) : (
              <div className="space-y-3">
                {digitalOrders.map(o => (
                  <div key={o.id} className="c4t-card flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#FFF0F7] rounded-xl flex items-center justify-center border-2 border-[#FF1F87]/20">
                        <FileText className="w-5 h-5 text-[#FF1F87]" />
                      </div>
                      <div>
                        <p className="font-['Fredoka'] font-bold">{o.product_name}</p>
                        <p className="text-xs c4t-desc flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {new Date(o.created_at).toLocaleDateString()}
                          <span className="mx-1">·</span>
                          {format(o.price)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(o.product_id, o.product_name)}
                      disabled={downloading[o.product_id]}
                      className="c4t-btn c4t-btn-pink flex items-center gap-2 !py-2 !text-sm disabled:opacity-60"
                    >
                      {downloading[o.product_id] ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {downloading[o.product_id] ? "Preparing…" : "Download"}
                    </button>
                  </div>
                ))}
                <p className="text-xs text-center c4t-desc mt-2">Download links are regenerated fresh each time. They never expire here.</p>
              </div>
            )
          )}
        </>
      )}

      {/* Danger Zone */}
      <div className="mt-16 border-t-2 border-dashed border-gray-200 pt-8">
        <h2 className="text-xl font-['Fredoka'] font-bold text-gray-500 mb-2">Danger Zone</h2>
        <p className="text-sm c4t-desc mb-4">
          Permanently delete your account. This removes all your personal data from our systems in accordance with our{" "}
          <Link to="/privacy" className="text-[#FF1F87] underline font-semibold">Privacy Policy</Link>.
        </p>
        <button onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2 border-[2px] border-[#FF1F87] text-[#FF1F87] font-bold rounded-xl px-5 py-2.5 text-sm hover:bg-[#FF1F87] hover:text-white transition-all shadow-[2px_2px_0_#FF1F87]">
          <Trash2 className="w-4 h-4" /> Delete My Account
        </button>
      </div>

      {reviewModal && (
        <ReviewModal item={reviewModal.item} orderId={reviewModal.orderId}
          onClose={() => setReviewModal(null)} onSuccess={markReviewed} />
      )}
      {showDeleteModal && (
        <DeleteAccountModal onClose={() => !deleting && setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount} deleting={deleting} />
      )}
    </div>
  );
}
